/**
 * IconButton — a square, icon-only action.
 *
 * Used by: table row actions (sửa / xóa), Modal and Drawer close buttons,
 * the Sidebar collapse toggle, the POS quantity steppers, DateRangePicker's
 * month arrows.
 *
 * Accessibility: `label` is REQUIRED and becomes both `aria-label` and the
 * native tooltip. An icon-only control without an accessible name is invisible
 * to a screen reader, so the type system refuses to let one be built.
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn, DISABLED, FOCUS_RING } from '../utils.js';

export type IconButtonVariant = 'ghost' | 'solid' | 'danger' | 'outline';
export type IconButtonSize = 'sm' | 'md' | 'touch';

const VARIANTS: Record<IconButtonVariant, string> = {
  ghost: 'text-fg-muted hover:bg-surface-alt hover:text-fg',
  solid: 'bg-brand-600 text-white hover:bg-brand-700',
  danger: 'text-danger-fg hover:bg-danger-bg',
  outline: 'ring-1 ring-inset ring-line-strong text-fg hover:bg-surface-alt',
};

const SIZES: Record<IconButtonSize, string> = {
  /** Dense desktop tables only — never on a POS or Staff surface. */
  sm: 'h-8 w-8 rounded-sm',
  md: 'h-touch w-touch rounded-md',
  touch: 'h-touch-lg w-touch-lg rounded-lg',
};

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'> {
  /** Accessible name. Required — see the note above. */
  readonly label: string;
  readonly icon: ReactNode;
  readonly variant?: IconButtonVariant;
  readonly size?: IconButtonSize;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition-colors',
        VARIANTS[variant],
        SIZES[size],
        FOCUS_RING,
        DISABLED,
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex items-center justify-center">
        {icon}
      </span>
    </button>
  );
});
